import { useEffect, useMemo, useState } from 'react'

const storage = typeof window !== 'undefined' ? window.localStorage : undefined

export type PersistedValueType = string | number | boolean | object

export const createPersitedKey = (family: string, key: string) =>
  `#${family}:${key}`

export const getPersistedItem = (
  key: string
): PersistedValueType | undefined => {
  const value = storage!.getItem(key)
  if (value) {
    return JSON.parse(value)
  }
}

export const setPersistedItem = (
  key: string,
  value: PersistedValueType
): void => {
  value = JSON.stringify(value)
  if (value !== undefined) {
    storage!.setItem(key, value)
  } else {
    removePersistedItem(key)
  }
}

export const removePersistedItem = (key: string): void => {
  storage!.removeItem(key)
}

export const usePersistedStorage = <TValueType = PersistedValueType>(
  persistedKey: string,
  defaultValue: TValueType
) => {
  const getValue = (): TValueType => {
    if (persistedKey) {
      const savedValue = getPersistedItem(persistedKey)
      return (savedValue ?? defaultValue) as TValueType
    } else {
      return defaultValue as TValueType
    }
  }

  const [value, setValue] = useState(getValue)

  useEffect(() => {
    const newValue = getValue()
    if (newValue !== value) {
      setValue(newValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedKey])

  useEffect(() => {
    if (!persistedKey) return
    setPersistedItem(persistedKey, value as PersistedValueType)
  }, [value, persistedKey])

  return useMemo(() => [value, setValue], [value]) as [
    TValueType,
    (value: TValueType) => void,
  ]
}
